import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, ArrowLeft, BookOpen, ChevronRight } from "lucide-react";


/**
 * BlueLedger — long-form user manual.
 *
 * Rendered on the public route `/manual` so anyone (customers, staff, prospects)
 * can bookmark and print it. Includes:
 *   • Sticky sidebar TOC with scroll-spy.
 *   • "Print / Save as PDF" button that uses `window.print()` — the print
 *     stylesheet in `index.css` hides chrome and expands the content.
 *   • Direct download of the raw markdown source (`/BlueLedger-User-Manual.md`).
 *
 * When updating: bump the version string in the header AND update the markdown
 * source at `/app/frontend/public/BlueLedger-User-Manual.md` so the downloadable
 * copy stays in sync.
 */
const VERSION = "1.0";
const UPDATED = "2026-06-26";


// Slug → title map is the source of truth for the TOC + section anchors.
// Ordered.
const SECTIONS = [
    ["getting-started", "Getting Started"],
    ["dashboard", "Your Dashboard"],
    ["customers", "Customers"],
    ["vendors", "Vendors"],
    ["items", "Items Catalog"],
    ["invoices", "Invoices"],
    ["estimates", "Estimates"],
    ["payments", "Payments"],
    ["bills", "Bills"],
    ["expenses", "Expenses"],
    ["receipts", "Receipts"],
    ["banking", "Banking"],
    ["reconciliation", "Reconciliation"],
    ["coa", "Chart of Accounts"],
    ["journal", "Journal Entries"],
    ["reports", "Reports"],
    ["data-hub", "Data Hub — Import & Export"],
    ["data-health", "Data Health — Tidy Up"],
    ["team", "Team & Roles"],
    ["audit", "Audit Log"],
    ["billing", "Billing & Plans"],
    ["settings", "Settings"],
    ["api", "Public API & Webhooks"],
    ["multi-currency", "Multi-Currency & FX"],
    ["cayman", "Cayman No-Tax Mode"],
    ["dark-mode", "Dark Mode"],
    ["tips", "Keyboard Shortcuts & Tips"],
    ["troubleshooting", "Troubleshooting & FAQ"],
];


export default function UserManual() {
    const [activeId, setActiveId] = useState(SECTIONS[0][0]);

    // Scroll-spy: highlight the TOC entry closest to the top of the viewport.
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActiveId(e.target.id);
                });
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
        );
        SECTIONS.forEach(([id]) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const handlePrint = () => window.print();

    return (
        <div className="min-h-screen bg-sand-50 text-ocean-700" data-testid="user-manual-page">
            {/* ----- Top bar (hidden on print) ----- */}
            <header className="print:hidden sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-sand-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-ocean-700"
                        data-testid="manual-back-home"
                    >
                        <ArrowLeft className="h-4 w-4" /> BlueLedger
                    </Link>
                    <div className="ml-auto flex items-center gap-2">
                        <a
                            href="/BlueLedger-User-Manual.md"
                            download
                            className="hidden sm:inline-flex"
                            data-testid="manual-download-md"
                        >
                            <Button variant="outline" size="sm" className="rounded-xl">
                                <FileDown className="h-4 w-4 mr-1.5" /> Download .md
                            </Button>
                        </a>
                        <Button
                            onClick={handlePrint}
                            size="sm"
                            className="bg-ocean-700 text-white rounded-xl"
                            data-testid="manual-print-btn"
                        >
                            <Printer className="h-4 w-4 mr-1.5" /> Print / Save as PDF
                        </Button>
                    </div>
                </div>
            </header>

            {/* ----- Page ----- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-14 print:py-0 print:px-0 print:max-w-none">
                <div className="grid lg:grid-cols-[240px,1fr] gap-10 print:block">
                    {/* ----- TOC (sticky sidebar) ----- */}
                    <nav
                        className="hidden lg:block sticky top-24 self-start max-h-[calc(100vh-8rem)] overflow-auto pr-2 print:hidden"
                        aria-label="Table of contents"
                    >
                        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-muted-foreground">
                            <BookOpen className="h-3.5 w-3.5" /> Table of contents
                        </div>
                        <ul className="space-y-0.5 text-sm">
                            {SECTIONS.map(([id, title], idx) => (
                                <li key={id}>
                                    <a
                                        href={`#${id}`}
                                        className={`block px-3 py-1.5 rounded-lg transition-colors ${activeId === id
                                            ? "bg-secondary/10 text-secondary-dark font-medium"
                                            : "text-muted-foreground hover:text-ocean-700 hover:bg-sand-100"
                                        }`}
                                        data-testid={`toc-${id}`}
                                    >
                                        <span className="text-xs text-muted-foreground/60 mr-2">{String(idx + 1).padStart(2, "0")}</span>
                                        {title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <article className="prose-manual">
                        <ManualCover />
                        <MobileToc activeId={activeId} />
                        <ManualBody />
                    </article>
                </div>
            </div>

            {/* ----- Print-only footer ----- */}
            <div className="hidden print:block print-footer">
                BlueLedger — User Manual v{VERSION} · {UPDATED} · https://blueledger.ws
            </div>
        </div>
    );
}


function ManualCover() {
    return (
        <section className="mb-14 print:mb-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-secondary-dark font-medium">User Manual</div>
            <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-heading text-ocean-700 leading-[1.05] print:text-4xl">
                Everything you can do <span className="text-secondary-dark">in BlueLedger.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                A modern accounting SaaS for small businesses, freelancers, and teams — with first-class Cayman Islands support and full multi-currency handling. This manual covers every feature.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sand-200 px-3 py-1">Version {VERSION}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sand-200 px-3 py-1">Updated {UPDATED}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sand-200 px-3 py-1">28 sections</span>
            </div>
        </section>
    );
}


function MobileToc({ activeId }) {
    return (
        <details className="lg:hidden print:hidden mb-10 rounded-2xl bg-white border border-sand-200 p-4">
            <summary className="cursor-pointer text-sm font-medium text-ocean-700 flex items-center gap-2">
                <BookOpen className="h-4 w-4" /> Contents ({SECTIONS.length} sections)
            </summary>
            <ul className="mt-3 space-y-0.5 text-sm">
                {SECTIONS.map(([id, title], idx) => (
                    <li key={id}>
                        <a
                            href={`#${id}`}
                            className={`block px-3 py-1.5 rounded-lg ${activeId === id ? "bg-secondary/10 text-secondary-dark" : "text-muted-foreground"}`}
                        >
                            <span className="text-xs text-muted-foreground/60 mr-2">{String(idx + 1).padStart(2, "0")}</span>
                            {title}
                        </a>
                    </li>
                ))}
            </ul>
        </details>
    );
}


// --- Body content ---
// Every section uses the same <Section> wrapper for consistent spacing and
// anchor behaviour. Content mirrors the markdown source verbatim (with light
// JSX formatting for code blocks / callouts).

const H = ({ id, children }) => (
    <h2 id={id} className="scroll-mt-24 mt-16 mb-4 text-2xl sm:text-3xl font-heading text-ocean-700 print:mt-8 print:text-2xl">
        {children}
    </h2>
);
const H3 = ({ children }) => (
    <h3 className="mt-8 mb-2 text-lg font-heading text-ocean-700">{children}</h3>
);
const P = ({ children }) => (
    <p className="text-[15px] leading-[1.75] text-ocean-700/85 mb-4">{children}</p>
);
const UL = ({ children }) => (
    <ul className="list-disc pl-6 space-y-1.5 text-[15px] leading-[1.75] text-ocean-700/85 mb-4 marker:text-secondary-dark">{children}</ul>
);
const OL = ({ children }) => (
    <ol className="list-decimal pl-6 space-y-1.5 text-[15px] leading-[1.75] text-ocean-700/85 mb-4 marker:text-muted-foreground">{children}</ol>
);
const Code = ({ children }) => (
    <code className="rounded bg-sand-200/60 px-1.5 py-0.5 font-mono text-[13px] text-ocean-700">{children}</code>
);
const Callout = ({ children }) => (
    <div className="my-5 rounded-xl bg-secondary/5 border border-secondary/20 px-4 py-3 text-[14px] leading-relaxed text-ocean-700/90">
        {children}
    </div>
);
const Pre = ({ children }) => (
    <pre className="my-4 rounded-xl bg-ocean-700 text-white text-[12.5px] leading-relaxed p-4 overflow-x-auto whitespace-pre">{children}</pre>
);


function ManualBody() {
    return (
        <>
            <H id="getting-started">1. Getting Started</H>
            <H3>Register</H3>
            <OL>
                <li>Go to <Code>/register</Code>.</li>
                <li>Enter your name, email, a strong password (12+ characters), your organization&apos;s name, country, and base currency.</li>
                <li>Cayman-based businesses should choose <strong>KY</strong> as country and <strong>KYD</strong> as currency — BlueLedger will automatically enable <em>Cayman No-Tax Mode</em>.</li>
                <li>We&apos;ll email you a verification link — click it before your first send-email action.</li>
            </OL>
            <H3>Sign in with two-factor authentication</H3>
            <P>Every login triggers a 6-digit one-time code delivered to your email. Enter it on the verification screen. Codes expire after 10 minutes.</P>
            <H3>Onboarding</H3>
            <P>On first login you&apos;ll be walked through organization details, logo upload, currency, and an optional Quick Start tour.</P>
            <H3>Password reset</H3>
            <P>Click <strong>Forgot password</strong> on the login page → enter your email. We email you a one-time reset link (expires in 30 minutes).</P>

            <H id="dashboard">2. Your Dashboard</H>
            <P>At-a-glance KPIs:</P>
            <UL>
                <li><strong>Revenue this month</strong> and comparison vs last month.</li>
                <li><strong>Cash in bank</strong> across all connected accounts.</li>
                <li><strong>Accounts Receivable / Payable</strong> — total unpaid, with an overdue flag.</li>
                <li><strong>Recent activity</strong> feed and top overdue invoices — click through to Send / Record payment.</li>
                <li><strong>Quick Start tour</strong> — click <em>Quick start</em> in the top bar any time.</li>
            </UL>

            <H id="customers">3. Customers</H>
            <H3>Create</H3>
            <P>Customers in the sidebar → <strong>+ New customer</strong>. Required: name. Optional: email, phone, billing address, tax ID, currency.</P>
            <H3>Statements</H3>
            <P>Open a customer → <strong>Statement of account</strong>. Pick a date range → generates opening balance + all invoices and payments + closing balance. <strong>Send statement</strong> emails it, with optional include-invoice-links toggle. Custom Reply-To address supported (defaults to your email).</P>
            <H3>Tidy up duplicates</H3>
            <P>Data Health → Duplicate customers. Fuzzy-matches names + emails and lets you merge duplicates into a single canonical record. All invoices and payments get re-linked automatically.</P>

            <H id="vendors">4. Vendors</H>
            <P>Same feature set as Customers — create, edit, delete, fuzzy duplicate detection + merge. Vendors are auto-created when you scan a bill or expense receipt.</P>

            <H id="items">5. Items Catalog</H>
            <P>Save products or services so line-item entry becomes single-click.</P>
            <UL>
                <li><strong>Items → + New item</strong>: name, description, unit price, income account, expense account.</li>
                <li>On any invoice or bill line, click the <strong>cube icon</strong> to search the catalog and auto-fill description + price.</li>
                <li>Items also support duplicate detection under Data Health.</li>
            </UL>

            <H id="invoices">6. Invoices</H>
            <H3>Create an invoice</H3>
            <OL>
                <li>Invoices → <strong>+ New invoice</strong>.</li>
                <li>Pick a customer (or create one on the fly).</li>
                <li>Add line items — description, quantity, unit price, tax %. Or pick from the item catalog.</li>
                <li>Optionally add notes (will appear on the printed / emailed invoice).</li>
                <li>Save as <strong>Draft</strong> or click Save + Send.</li>
            </OL>
            <Callout>
                <strong>Description column.</strong> Bill + invoice list views show the joined line-item descriptions in a new <em>Description</em> column so you don&apos;t need to open a document to see what it&apos;s for. Hover to see the full untruncated text.
            </Callout>
            <H3>Send</H3>
            <P>Click the paper-plane icon on a Draft. The <strong>Send dialog</strong> shows the resolved recipient email and a <strong>Reply-To</strong> input (defaults to your email). Customise the message body in Settings → Outbound email messages.</P>
            <H3>Record a payment</H3>
            <P>Click the <strong>$</strong> icon → amount, method (cash/card/bank/cheque), paid-on date, reference, and bank account to deposit into. Tick <strong>Email receipt</strong> to send a confirmation in the same action.</P>
            <H3>Payment receipts</H3>
            <P>After a payment is recorded, click the <strong>receipt icon</strong> on the invoice row to view / edit / delete individual payments and re-send a receipt.</P>
            <H3>Duplicate / Recurring / Convert / Delete</H3>
            <UL>
                <li><strong>Duplicate</strong>: copy icon → new draft with today&apos;s date and a fresh number.</li>
                <li><strong>Recurring</strong>: tick <em>Make this invoice recurring</em> on edit. Pick frequency (weekly/monthly/quarterly/annually) and end date. BlueLedger auto-creates a fresh draft and emails it to the customer (Pro/Team).</li>
                <li><strong>Convert</strong>: convert an estimate to an invoice with one click.</li>
                <li><strong>Delete</strong>: trash icon cascades delete of all payments attached, after confirmation.</li>
            </UL>

            <H id="estimates">7. Estimates</H>
            <P>Same as invoices, but with a lifecycle: <Code>Open → Converted or Void</Code>. Click <strong>Convert</strong> on the row to spawn a real invoice (identical lines) — the estimate is marked <em>Converted</em> for audit.</P>

            <H id="payments">8. Payments</H>
            <P>Chronological log of every payment (invoice + bill) across every currency. Filter by date range, customer/vendor, method, or bank account. Editing a payment automatically rolls back the linked doc&apos;s balance and adjusts the bank account.</P>

            <H id="bills">9. Bills</H>
            <H3>Create</H3>
            <P>Bills → <strong>+ New bill</strong>. Vendor + issue/due dates + currency + line items. Optionally attach the original PDF or photo (paperclip icon on the row lets anyone in the org view it later).</P>
            <H3>Scan a bill with AI (OCR)</H3>
            <UL>
                <li>Bills → <strong>Scan bill</strong>.</li>
                <li>Drag & drop or pick a PDF or image (up to 5 pages).</li>
                <li>GPT-4o vision reads the bill and pre-fills vendor, dates, line items, currency, and totals.</li>
                <li>Unknown vendors are <strong>auto-created</strong> with the note &quot;Auto-created by bill scan&quot;.</li>
                <li>OCR usage counts toward your monthly quota (5 on Free, 100 on Pro, 500 on Team).</li>
            </UL>
            <H3>Pay a bill</H3>
            <P>Click the <strong>$</strong> icon → amount, method, paid-on, reference, bank account to withdraw from. Optionally attach a receipt scan to the payment.</P>
            <H3>View / edit bill payments</H3>
            <P>Click the receipt icon on a paid or partial bill to see all payments. Editing (amount, account, method) requires a <strong>reason note</strong> for the audit log. Deleting rolls back the bill balance automatically.</P>
            <H3>Duplicate detection</H3>
            <P>Data Health → Duplicate bills groups bills sharing (vendor, issue date, total) so you can spot double-entries.</P>

            <H id="expenses">10. Expenses</H>
            <P>Lightweight one-off transactions — no terms or payable balance.</P>
            <UL>
                <li><strong>Expenses → + New expense</strong>: vendor, date, amount, tax, category, description, currency, payment method.</li>
                <li><strong>Scan expense</strong> works like Scan bill but produces a single-line expense.</li>
                <li>Attach a photo of the receipt. Categories are user-defined and reused across the app.</li>
            </UL>

            <H id="receipts">11. Receipts</H>
            <P>Standalone customer-facing payment receipts you&apos;ve uploaded — used with the OCR flow when you don&apos;t need a full bill/expense structure. Up to 6 MB per receipt. The AI parses merchant, date, total, tax, currency, category, and line items.</P>

            <H id="banking">12. Banking</H>
            <H3>Bank accounts</H3>
            <P>Banking → <strong>+ New bank account</strong>: name, currency, opening balance, opening date. Multi-currency supported — hold KYD, USD, GBP, EUR side by side.</P>
            <H3>Bank transactions</H3>
            <UL>
                <li>Click an account → ledger opens with all its transactions.</li>
                <li><strong>+ New transaction</strong> for manual deposits, withdrawals, transfers, fees.</li>
                <li>Edit / delete any transaction (with audit reason note).</li>
                <li>Attach transactions to an invoice, bill, expense, or journal entry to keep everything reconcilable.</li>
            </UL>
            <H3>Transfers</H3>
            <P><strong>Transfer between accounts</strong>: source + destination + amount + FX rate → matched withdrawal + deposit are created atomically.</P>

            <H id="reconciliation">13. Reconciliation</H>
            <OL>
                <li>Reconciliation → pick a bank account, statement end date, and ending balance.</li>
                <li><strong>Smart matching</strong> auto-links your recorded bank transactions to statement lines based on amount and date proximity.</li>
                <li>Tick each match, resolve unmatched items (add missing or write off small differences), then <strong>Lock reconciliation</strong>.</li>
                <li>Locked-in transactions can no longer be edited without unlocking.</li>
            </OL>

            <H id="coa">14. Chart of Accounts</H>
            <P>BlueLedger seeds a standard Chart of Accounts (Assets, Liabilities, Equity, Revenue, Expenses) on first login. You can rename, add sub-accounts up to two levels deep, archive unused accounts (hidden but preserved for reports), and change type/subtype with confirmation.</P>

            <H id="journal">15. Journal Entries</H>
            <OL>
                <li>Journal Entries → <strong>+ New entry</strong>.</li>
                <li>Date, memo, and at least two lines. Debits and credits must balance.</li>
                <li>Save as Draft or Post immediately.</li>
                <li>Posted entries flow into all reports.</li>
            </OL>

            <H id="reports">16. Reports</H>
            <P>Every report supports date range picker with presets (This month / Last month / This quarter / YTD / Custom), compare-to-period toggle, CSV + PDF export, and multi-currency totals converted to your org&apos;s base currency.</P>
            <UL>
                <li><strong>Profit &amp; Loss</strong></li>
                <li><strong>Balance Sheet</strong></li>
                <li><strong>Cash Flow Statement</strong></li>
                <li><strong>Accounts Receivable Aging</strong></li>
                <li><strong>Accounts Payable Aging</strong></li>
                <li><strong>Tax Summary</strong> (blank in Cayman No-Tax Mode)</li>
                <li><strong>Sales by Customer / Item / Category</strong></li>
                <li><strong>Expenses by Vendor / Category</strong></li>
                <li><strong>Journal Report</strong> · <strong>Trial Balance</strong></li>
            </UL>

            <H id="data-hub">17. Data Hub — Import &amp; Export</H>
            <H3>Import</H3>
            <P>CSV templates provided for Customers, Vendors, Items, Invoices, Bills, Expenses, Bank Transactions, Chart of Accounts, and Journal Entries. Upload → validate → preview → confirm.</P>
            <H3>Export</H3>
            <P>Every entity supports Export CSV and Export JSON. <strong>Export everything</strong> gives you a single ZIP full backup of your org.</P>

            <H id="data-health">18. Data Health — Tidy Up</H>
            <P>Real-time diagnostics of your books:</P>
            <UL>
                <li>Duplicate customers / vendors / items — one-click merge.</li>
                <li>Duplicate bills flagged by (vendor, date, total).</li>
                <li>Orphan payments (invoice/bill deleted).</li>
                <li>Unreconciled bank transactions older than 30 days.</li>
                <li>Missing categories on expenses.</li>
            </UL>

            <H id="team">19. Team &amp; Roles</H>
            <H3>Roles</H3>
            <UL>
                <li><strong>Owner</strong> — full access, can delete the organization.</li>
                <li><strong>Admin</strong> — full access except deleting the organization.</li>
                <li><strong>Bookkeeper</strong> — everything except managing members and billing.</li>
                <li><strong>Viewer</strong> — read-only.</li>
            </UL>
            <H3>Invite a member</H3>
            <P>+ Invite member → email, role, optional expiry date. Existing BlueLedger users see the org in their <strong>Shared with you</strong> section of the org switcher; new users are prompted to create an account first.</P>

            <H id="audit">20. Audit Log</H>
            <P>Every mutation is recorded: who did what, when, on which entity. Sensitive actions (payment edits, deletions) capture a reason. Filter by user, action, or entity type. Export CSV.</P>

            <H id="billing">21. Billing &amp; Plans</H>
            <H3>Plans</H3>
            <UL>
                <li><strong>Free</strong> — 2 customers, 10 invoices/month, 5 OCR scans/month, no API, no white-label.</li>
                <li><strong>Pro</strong> — unlimited customers/invoices, 100 OCR scans/month, API + webhooks, white-label opt-out.</li>
                <li><strong>Team</strong> — everything in Pro plus 500 OCR scans/month and up to 20 team members.</li>
            </UL>
            <H3>Upgrade</H3>
            <P>Click <strong>Upgrade</strong> → PayPal checkout in a new tab. Subscription is monthly with instant activation.</P>
            <H3>Universal Key (AI usage)</H3>
            <P>Some AI features (Assistant chat, OCR) draw from the built-in Emergent Universal Key. Balance visible in Settings → AI usage. Top up via <strong>Profile → Universal Key → Add Balance</strong>, or enable Auto top-up.</P>

            <H id="settings">22. Settings</H>
            <UL>
                <li><strong>Organization details</strong> — name, address, tax ID, contact email, logo.</li>
                <li><strong>Currency &amp; FX</strong> — default currency, FX rate overrides.</li>
                <li><strong>Default payment terms</strong> — auto-appended to invoice notes.</li>
                <li><strong>Outbound email messages</strong> — customise the human message on invoice/estimate/receipt/statement emails. Leave blank for defaults.</li>
                <li><strong>Branding &amp; white-label</strong> — hide the &quot;Made with BlueLedger&quot; footer (Pro/Team).</li>
                <li><strong>API &amp; Integrations</strong> — see next section.</li>
                <li><strong>Danger zone</strong> — export a final backup, then delete the org.</li>
            </UL>

            <H id="api">23. Public API &amp; Webhooks</H>
            <Callout>Available on <strong>Pro</strong> and <strong>Team</strong> plans.</Callout>
            <H3>Personal API tokens</H3>
            <UL>
                <li>Settings → API &amp; Integrations → <strong>+ New token</strong>.</li>
                <li>Pick an <strong>Access level</strong>:</li>
            </UL>
            <UL>
                <li><Code>read</Code> — GET <Code>/api/v1/*</Code> only. Reporting tools, BI dashboards.</li>
                <li><Code>write</Code> — Read + create / edit / record payments on invoices, customers, vendors, items, bills, expenses. Zapier, order-syncing tools.</li>
                <li><Code>admin</Code> — Write + send-email + delete. Trusted internal scripts only.</li>
            </UL>
            <UL>
                <li>Tokens start with <Code>blk_live_</Code> and are shown <strong>once only</strong>. SHA-256 hashed at rest.</li>
                <li>Rate-limited per token: <strong>120/min for reads, 60/min for writes</strong>. Exceeding returns 429 with a <Code>Retry-After</Code> header.</li>
                <li>Revoke any time — revoked tokens return 401 immediately.</li>
            </UL>
            <H3>Read API</H3>
            <P>Send <Code>Authorization: Bearer blk_live_...</Code> on any request to <Code>https://blueledger.ws/api/v1/...</Code></P>
            <UL>
                <li><Code>GET /api/v1/me</Code> — identify org + token (+ scope)</li>
                <li><Code>GET /api/v1/invoices</Code> / <Code>/invoices/&#123;id&#125;</Code></li>
                <li><Code>GET /api/v1/customers</Code>, <Code>/vendors</Code>, <Code>/items</Code>, <Code>/payments</Code>, <Code>/bills</Code></li>
                <li>All list endpoints support <Code>?limit=</Code> and <Code>?offset=</Code>.</li>
            </UL>
            <H3>Write API</H3>
            <P>All endpoints below require a token with <Code>write</Code> scope (or higher). Send/delete require <Code>admin</Code>.</P>
            <UL>
                <li><Code>POST /api/v1/customers</Code>, <Code>/vendors</Code>, <Code>/items</Code> — create records.</li>
                <li><Code>PATCH /api/v1/customers/&#123;id&#125;</Code> — edit.</li>
                <li><Code>DELETE /api/v1/customers/&#123;id&#125;</Code> — delete.</li>
                <li><Code>POST /api/v1/invoices</Code> — create draft invoice with line items.</li>
                <li><Code>PATCH /api/v1/invoices/&#123;id&#125;</Code> — edit invoice.</li>
                <li><Code>POST /api/v1/invoices/&#123;id&#125;/payments</Code> — record a payment (updates balance + status automatically).</li>
                <li><Code>POST /api/v1/invoices/&#123;id&#125;/duplicate</Code> — clone as a new draft.</li>
                <li><Code>POST /api/v1/invoices/&#123;id&#125;/send</Code> — email the invoice to the customer <em>(admin scope)</em>.</li>
                <li><Code>DELETE /api/v1/invoices/&#123;id&#125;</Code> — hard-delete + cascade payments <em>(admin scope)</em>.</li>
                <li><Code>POST /api/v1/bills</Code>, <Code>PATCH</Code>, <Code>POST .../pay</Code>, <Code>DELETE</Code> — same pattern.</li>
                <li><Code>POST /api/v1/expenses</Code>, <Code>PATCH</Code>, <Code>DELETE</Code>.</li>
            </UL>
            <H3>Webhook signing secret</H3>
            <P>Settings → API &amp; Integrations → Webhook signing secret. Click <strong>Reveal</strong> → <strong>Copy</strong>. Rotate at any time to invalidate old configs.</P>
            <H3>WooCommerce quick start</H3>
            <OL>
                <li>WP Admin → WooCommerce → Settings → Advanced → <strong>Webhooks → Add webhook</strong>.</li>
                <li>Topic: <strong>Order updated</strong> (or Order created). Status: <strong>Active</strong>.</li>
                <li>Delivery URL: paste the WooCommerce URL from BlueLedger.</li>
                <li>Secret: paste the BlueLedger webhook secret.</li>
                <li>Save. WooCommerce will deliver completed orders — each becomes a paid invoice with the customer auto-created (matched by email).</li>
            </OL>
            <H3>Shopify quick start</H3>
            <OL>
                <li>Shopify admin → <strong>Settings → Notifications → Webhooks → Create webhook</strong>.</li>
                <li>Event: <strong>Order paid</strong>. Format: <strong>JSON</strong>.</li>
                <li>URL: paste the Shopify URL from BlueLedger.</li>
                <li>Rotate the BlueLedger secret to match Shopify&apos;s signing secret (or vice versa).</li>
                <li>Save + click <strong>Send test notification</strong>. BlueLedger acknowledges the empty test payload with 200. Then place a paid test order — it&apos;ll land as a paid invoice with shipping and line items intact.</li>
            </OL>
            <H3>Generic webhook format</H3>
            <Pre>{`POST /api/webhooks/sales/{org_id}
Content-Type: application/json
X-BL-Signature: <base64 HMAC-SHA256 of raw body>

{
  "external_id": "order-1234",
  "customer_email": "buyer@example.com",
  "customer_name": "Jane Buyer",
  "lines": [
    {"description": "Widget", "quantity": 2, "unit_price": 12.50},
    {"description": "Sticker", "quantity": 1, "unit_price": 3.00}
  ],
  "total": 28.00,
  "currency": "USD",
  "paid_on": "2026-06-26",
  "reference": "PO-1001",
  "method": "card"
}`}</Pre>
            <UL>
                <li>Signature = <Code>base64(hmac_sha256(raw_body, secret))</Code>.</li>
                <li><strong>Idempotency</strong>: <Code>external_id</Code> + <Code>source</Code> are the dedupe key — safe to retry.</li>
                <li>Response: <Code>{`{ok: true, invoice_id, invoice_number, duplicate}`}</Code></li>
            </UL>

            <H id="multi-currency">24. Multi-Currency &amp; FX</H>
            <UL>
                <li>Every invoice, bill, payment, and bank account has its own currency.</li>
                <li>Base currency is set at the org level.</li>
                <li>Foreign-currency documents store both the document total and the base-currency-equivalent total.</li>
                <li>The FX chip on list views shows the rate at a glance.</li>
                <li>Reports always roll up in base currency.</li>
            </UL>

            <H id="cayman">25. Cayman No-Tax Mode</H>
            <P>Auto-enabled when the org&apos;s country is <strong>KY</strong> (also toggleable in Settings). Tax fields on invoices/bills are hidden and forced to 0%. Tax Summary report is disabled. Documents display a &quot;Cayman · No-tax mode&quot; chip in the top bar.</P>

            <H id="dark-mode">26. Dark Mode</H>
            <P>Toggle from the sun/moon icon in the top bar. Preference is remembered per device. Dark mode is optimised for long sessions: reduced-contrast cards, softened shadows, inverted native date picker icons, dark-mode friendly email previews.</P>

            <H id="tips">27. Keyboard Shortcuts &amp; Tips</H>
            <UL>
                <li><Code>⌘/Ctrl + K</Code> — open the command palette (jump to any page).</li>
                <li><Code>?</Code> — show shortcuts overlay.</li>
                <li>Any list view: click a column header to sort.</li>
                <li>Any table row: middle-click a link to open in a new tab.</li>
                <li>Hover the <strong>Description</strong> column cell on Bills / Invoices for the full text.</li>
            </UL>

            <H id="troubleshooting">28. Troubleshooting &amp; FAQ</H>
            <H3>Cursor jumps out of an email-message text box in Settings.</H3>
            <P>Fixed in v1.0.5. If you still see it, hard-refresh the browser (<Code>Cmd/Ctrl + Shift + R</Code>).</P>
            <H3>WooCommerce says &quot;Delivery URL returned response code: 401&quot; on save.</H3>
            <P>Fixed in v1.0.6 — WooCommerce sends an unsigned validation ping on save, which we now acknowledge. If you still hit 401 after redeploy, double-check the secret matches exactly (no whitespace, correct env).</P>
            <H3>Paid invoices aren&apos;t showing in Bank reconciliation.</H3>
            <P>Ensure the payment was recorded against a <strong>specific bank account</strong>, not &quot;Undeposited funds&quot;. Edit the payment, set the correct account, and it&apos;ll appear.</P>
            <H3>An added customer isn&apos;t showing in the Customers list.</H3>
            <P>Fixed in v1.0.4 pagination refactor. On older builds, hard-refresh; list caps out at 5000 rows.</P>
            <H3>How do I export everything before I close my org?</H3>
            <P>Settings → Danger zone → <strong>Export everything</strong>. You get a ZIP with all data as CSVs. Save it before you click Delete.</P>
            <H3>How do I get the manual as a PDF?</H3>
            <P>Click <strong>Print / Save as PDF</strong> in the top-right — the layout is print-optimised. Or download the raw markdown source with <strong>Download .md</strong>.</P>

            <hr className="my-14 border-sand-200" />
            <p className="text-sm text-muted-foreground">
                Need help? <Link className="text-secondary-dark hover:underline inline-flex items-center gap-0.5" to="/app/help">Visit the Help Center <ChevronRight className="h-3 w-3" /></Link> or email <a className="text-secondary-dark hover:underline" href="mailto:support@blueledger.ws">support@blueledger.ws</a>.
            </p>
            <p className="mt-4 text-xs text-muted-foreground/70">© 2026 BlueLedger. Made with love in the Cayman Islands.</p>
        </>
    );
}
